import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCallback } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingTitle } from '@/components/ui/page-heading';
import { CONTROL_TYPES, ControlType } from '@/constants/institution';
import InstitutionAlgorithm from '@/features/institution/futures/components/algorithm';
import InstitutionFuturesIndividuals from '@/features/institution/futures/components/individuals';
import {
  type InstitutionFuturesExecution,
  type InstitutionFuturesExecutionOrDefaults,
  institutionDetailsExecutionApiToInstitutionFuturesExecution,
  institutionFuturesExecutionSchema,
  institutionFuturesExecutionToInstitutionDetailsExecutionApi,
} from '@/features/institution/futures/execution/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { reportIndividualNationalClientIdentifierFormatErrors } from '@/features/institution/schemas/individual';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';

const InstitutionFuturesExecutionFormForm = ({ defaultValues }: { defaultValues: InstitutionFuturesExecutionOrDefaults }) => {
  const form = useForm<InstitutionFuturesExecution>({
    defaultValues,
    resolver: standardSchemaResolver(institutionFuturesExecutionSchema),
  });
  const { isPending, mutate } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const execution = form.watch('execution');

  const onSubmit = (data: InstitutionFuturesExecution) => {
    mutate(
      { execution: institutionFuturesExecutionToInstitutionDetailsExecutionApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/institution/futures/client-categorisation', { replace: true });
        },
      },
    );
  };

  const onInvalid = () => reportIndividualNationalClientIdentifierFormatErrors(form.getValues('individuals'));

  return (
    <Form {...form}>
      <form name="institution-execution" onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            name="execution"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.execution.execution')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.execution.execution')}
                    onValueChange={(value) => {
                      field.onChange(value);
                      (form as UseFormReturn<InstitutionFuturesExecutionOrDefaults>).setValue(value === ControlType.INDIVIDUAL ? 'algorithm' : 'individuals', undefined);
                    }}
                  >
                    {CONTROL_TYPES.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`institutionDetails.controlType.options.${answer}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {{
            [ControlType.ALGORITHM]: <InstitutionAlgorithm />,
            [ControlType.INDIVIDUAL]: <InstitutionFuturesIndividuals section="execution" />,
          }[execution] ?? null}
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
InstitutionFuturesExecutionFormForm.displayName = 'InstitutionFuturesExecutionFormForm';

const InstitutionFuturesExecutionForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.FUTURES, 'execution');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const handlePrevStep = useCallback(() => {
    navigate('/institution/futures/decision-maker', { replace: true });
  }, [navigate]);

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.execution ? institutionDetailsExecutionApiToInstitutionFuturesExecution(institutionDetailsAnswers.execution) : {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.execution.title')}</PageHeadingTitle>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />
      <PageCard size="lg" bordered fixedFooter>
        <InstitutionFuturesExecutionFormForm defaultValues={defaultValues} />
      </PageCard>
    </section>
  );
};
InstitutionFuturesExecutionForm.displayName = 'InstitutionFuturesExecutionForm';

export default InstitutionFuturesExecutionForm;
